import { EngagementDocumentType, EngagementStep, EngagementStepStatus, EngagementStepType, EngagementTaskType } from '@aiwyn/api';
import { InputSignal } from '@angular/core';
import { Observable } from 'rxjs';

export enum FooterButtonType {
  PREVIOUS_FIELD = 'previousField',
  NEXT_FIELD = 'nextField',
  PREVIOUS_STEP = 'previousStep',
  NEXT_STEP = 'nextStep',
  APPROVE_AND_SUBMIT = 'approveAndSubmit',
  COMPLETE_ENGAGEMENT = 'completeEngagement',
  CLOSE_ENGAGEMENT = 'closeEngagement',
  UPDATE_PAYMENT_METHOD = 'updatePaymentMethod',
}

export enum EngagementStepLabel {
  LETTER = 'Engagement Letter',
  PAYMENT = 'Payment Information',
  CONSENT = 'Consent Form',
  ADD_ON = 'Add On Service',
}

export interface EngagementStepRef {
  type: EngagementStepType;
  documentId?: number;
}

export const ENGAGEMENT_STEP_LABEL_MAP: Record<EngagementStepType, EngagementStepLabel | ((name: string) => string)> = {
  [EngagementStepType.LETTER]: EngagementStepLabel.LETTER,
  [EngagementStepType.PAYMENT]: EngagementStepLabel.PAYMENT,
  [EngagementStepType.CONSENT]: EngagementStepLabel.CONSENT,
  [EngagementStepType.ADD_ON]: (label?: string) => label || EngagementStepLabel.ADD_ON,
};

export enum ConsentValues {
  AFFIRMATIVE = 'Affirmative',
  NEGATIVE = 'Negative',
  OFF = 'Off',
}

export interface EngagementStepComponent {
  details: EngagementStep | InputSignal<EngagementStep>;
  status: EngagementStepStatus;
  getFooterButtons: () => Observable<FooterButtonType[]>;
  /**
   * Success: true
   * Failure: false
   * Invalid: null
   */
  submit: (isMobile?: boolean) => Observable<EngagementStepSubmissionResult>;
}

export interface EngagementStepSubmissionResult {
  success: boolean;
  errorMessage?: string; // EngagementStepSubmissionFailureMessage;
}

export enum EngagementStepSubmissionFailureMessage {
  ENGAGEMENT_LETTER_OUTDATED = 'I BET YOU NO ONE WILL SEE THESE.',
  ENGAGEMENT_LETTER_UNASSIGNED = 'HOW CAN YOU SEE THIS?',
  ENGAGEMENT_LETTER_DELETED = 'THAT WASN\'T SUPPOSED TO HAPPEN!',
  ENGAGEMENT_LETTER_CANCELED = 'HOW DO YOU SPELL CANSOLD?',
  ENGAGEMENT_LETTER_UNDER_REVISION = 'THESE MESSAGES ARE UNDER REVISION.',
}

export interface EngagementStepDisplay extends EngagementStep {
  label: string;
}

export interface EngagementLetterStep extends EngagementStep {
  type: EngagementStepType.LETTER;
}

export interface ConsentDocumentStep extends EngagementStep {
  type: EngagementStepType.CONSENT;
}

export interface AddOnDocumentStep extends EngagementStep {
  type: EngagementStepType.ADD_ON;
}

export const ENGAGEMENT_STEP_FAILED_MESSAGE: Record<EngagementStepType, string> = {
  [EngagementStepType.LETTER]: 'ACTION REQUIRED', // this should never show, but keeping here for type adherence and in case we ever need it
  [EngagementStepType.PAYMENT]: 'PAYMENT FAILED',
  [EngagementStepType.CONSENT]: 'ACTION REQUIRED',
  [EngagementStepType.ADD_ON]: 'ACTION REQUIRED',
};

export type DestinationEngagementStepType = EngagementStepType | 'previous' | 'next';
export type DestinationEngagementStepRef = EngagementStepRef | 'previous' | 'next';

export interface EngagementsCurrentStepType {
  [engagementId: number]: EngagementStepType;
}

export interface EngagementsCurrentStepRef {
  [engagementId: number]: EngagementStepRef;
}

export const STEP_LABEL_DOCUMENT_TYPE_MAPPING: { [K in Exclude<EngagementStepType, EngagementStepType.PAYMENT>]: EngagementDocumentType } = {
  [EngagementStepType.CONSENT]: EngagementDocumentType.CONSENT_DOCUMENT,
  [EngagementStepType.LETTER]: EngagementDocumentType.ENGAGEMENT_LETTER,
  [EngagementStepType.ADD_ON]: EngagementDocumentType.ADDON_DOCUMENT,
}

export const ENGAGEMENT_STEP_TASKS: Record<EngagementStepType, { index: number, taskTypes: EngagementTaskType[] }> = {
  [EngagementStepType.LETTER]: {
    index: 0,
    taskTypes: [EngagementTaskType.SIGN_ENGAGEMENT_DOCUMENT, EngagementTaskType.VIEW_ENGAGEMENT],
  },
  [EngagementStepType.CONSENT]: {
    index: 1,
    taskTypes: [EngagementTaskType.PROVIDE_CONSENT, EngagementTaskType.CONFIRM_CONSENT_TASK],
  },
  [EngagementStepType.ADD_ON]: {
    index: 2,
    taskTypes: [EngagementTaskType.PROVIDE_ADD_ON_TASK, EngagementTaskType.CONFIRM_ADD_ON_TASK],
  },
  [EngagementStepType.PAYMENT]: {
    index: 3,
    taskTypes: [EngagementTaskType.PROVIDE_PAYMENT_INFO, EngagementTaskType.CONFIRM_PAYMENT_INFO],
  },
};
